import { useMemo, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import {
  Tabs,
  TabsContent,
  TabsCount,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import {
  TIMELINE_BEAT_KINDS,
  TIMELINE_BEAT_KIND_LABELS,
  TIMELINE_DATE_PRECISIONS,
  TIMELINE_ISSUE_KIND_LABELS,
  TIMELINE_ISSUE_SEVERITY_LABELS,
  TIMELINE_NARRATIVE_POSITIONS,
  issuesForBeat,
  problematicIssues,
  sortBeatsByStoryOrder,
  sortBeatsChronologically,
  underDescribedIssues,
  type BookTimelineBeat,
  type TimelineBeatKind,
  type TimelineDatePrecision,
  type TimelineNarrativePosition,
  type TimelineRemovalRecord,
  type TimelineTimingIssue,
} from "../../../shared/timelineRoster";
import { cn } from "../../lib/utils";

/** Beat kinds use cool tones; timing issues use warm flags — never the same red. */
const BEAT_KIND_COLORS: Record<TimelineBeatKind, string> = {
  plot: "#2563eb",
  action: "#0f766e",
  character: "#7c3aed",
};

type GraphOrderMode = "chrono" | "story";
type IssueFilter = "all" | "problems" | "under_described";
type SpanSeverity = "problem" | "under" | "warn" | null;

function BeatKindBadge({ kind }: { kind: TimelineBeatKind }) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "h-5 rounded-md px-1.5 text-[0.625rem] font-semibold uppercase tracking-wider",
        kind === "plot" && "bg-blue-500/15 text-blue-700 dark:text-blue-300",
        kind === "action" && "bg-teal-500/15 text-teal-700 dark:text-teal-300",
        kind === "character" &&
          "bg-violet-500/15 text-violet-700 dark:text-violet-300",
      )}
    >
      {TIMELINE_BEAT_KIND_LABELS[kind]}
    </Badge>
  );
}

function SeverityBadge({
  severity,
}: {
  severity: TimelineTimingIssue["severity"];
}) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "h-5 shrink-0 rounded-md px-1.5 text-[0.625rem] font-semibold uppercase tracking-wider",
        severity === "problem" && "bg-destructive/15 text-destructive",
        severity === "warn" &&
          "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        severity === "info" &&
          "bg-slate-500/15 text-slate-700 dark:text-slate-300",
      )}
    >
      {TIMELINE_ISSUE_SEVERITY_LABELS[severity]}
    </Badge>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.03em] text-muted-foreground">
      {children}
    </span>
  );
}

function truncate(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
}

function spanSeverityForPair(
  issues: TimelineTimingIssue[],
  a: BookTimelineBeat,
  b: BookTimelineBeat,
): { severity: SpanSeverity; issue: TimelineTimingIssue | null } {
  const spanIssues = issues.filter(
    (issue) =>
      issue.beatIds.includes(a.id) &&
      (issue.beatIds.includes(b.id) || issue.beatIds.length === 1),
  );
  if (spanIssues.length === 0) return { severity: null, issue: null };
  const worst = spanIssues.some(
    (iss) =>
      iss.severity === "problem" ||
      iss.kind === "contradiction" ||
      iss.kind === "compressed" ||
      iss.kind === "chronology_conflict",
  )
    ? "problem"
    : spanIssues.some(
          (iss) => iss.kind === "under_specified" || iss.kind === "gap",
        )
      ? "under"
      : "warn";
  return { severity: worst, issue: spanIssues[0]! };
}

function BeatChronologyRail({
  beats,
  issues,
  chapters,
  orderMode,
  selectedBeatId,
  selectedIssueId,
  onSelectBeat,
  onSelectIssue,
}: {
  beats: BookTimelineBeat[];
  issues: TimelineTimingIssue[];
  chapters: Array<{ id: string; order: number; title: string }>;
  orderMode: GraphOrderMode;
  selectedBeatId: string | null;
  selectedIssueId: string | null;
  onSelectBeat: (id: string) => void;
  onSelectIssue: (id: string) => void;
}) {
  const chapterOrder = useMemo(() => {
    const map = new Map<string, number>();
    for (const ch of chapters) map.set(ch.id, ch.order);
    return map;
  }, [chapters]);

  const ordered = useMemo(
    () =>
      orderMode === "story"
        ? sortBeatsByStoryOrder(beats, chapterOrder)
        : sortBeatsChronologically(beats, chapterOrder),
    [beats, chapterOrder, orderMode],
  );

  const chapterTitle = (id: string) =>
    chapters.find((c) => c.id === id)?.title ?? "";

  if (ordered.length === 0) {
    return (
      <EmptyState
        compact
        className="mx-4 my-3"
        title="No major beats yet"
        description="Extract from a chapter Timeline tab, or run Analyze whole book."
      />
    );
  }

  return (
    <div className="border-t border-border bg-card px-4 pb-2 pt-3">
      <div
        className="flex snap-x snap-proximity items-stretch overflow-x-auto pb-1.5"
        role="list"
        aria-label="Major beat chronology"
      >
        {ordered.map((beat, i) => {
          const selected = selectedBeatId === beat.id;
          const isFlashback =
            beat.narrativePosition === "flashback" ||
            beat.narrativePosition === "recalled";
          const next = ordered[i + 1];
          const span = next
            ? spanSeverityForPair(issues, beat, next)
            : { severity: null, issue: null };
          const whenLabel = beat.when || beat.sortKey || `Beat ${i + 1}`;
          const chLabel = chapterTitle(beat.chapterId);

          return (
            <div
              key={beat.id}
              className="flex shrink-0 snap-start items-stretch"
              role="listitem"
            >
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "h-auto min-h-27 w-46 flex-col items-start justify-start gap-1.5 whitespace-normal rounded-lg border-t-[3px] bg-card px-3 py-2.5 text-left text-foreground shadow-none hover:bg-muted/30",
                  isFlashback && "border-dashed",
                  selected && "border-primary ring-1 ring-primary/45",
                )}
                style={{ borderTopColor: BEAT_KIND_COLORS[beat.beatKind] }}
                onClick={() => onSelectBeat(beat.id)}
                aria-pressed={selected}
                title={beat.summary}
              >
                <span className="line-clamp-2 text-[0.6875rem] font-semibold uppercase leading-tight tracking-[0.02em] text-muted-foreground">
                  {whenLabel}
                </span>
                <span className="line-clamp-3 flex-1 text-[0.8125rem] font-semibold leading-snug">
                  {beat.summary}
                </span>
                <span className="mt-auto flex flex-wrap items-center gap-1.5">
                  <BeatKindBadge kind={beat.beatKind} />
                  {chLabel ? (
                    <span className="text-[0.625rem] text-muted-foreground">
                      {chLabel}
                    </span>
                  ) : null}
                  {isFlashback ? (
                    <span className="text-[0.625rem] italic text-muted-foreground">
                      Flashback
                    </span>
                  ) : null}
                </span>
              </Button>

              {next ? (
                <div
                  className="relative flex w-9 shrink-0 flex-col items-center justify-center"
                  aria-hidden={!span.issue}
                >
                  <span
                    className={cn(
                      "absolute inset-x-0 top-1/2 h-0.5 bg-border",
                      span.severity === "problem" && "h-0.75 bg-destructive",
                      (span.severity === "under" || span.severity === "warn") &&
                        "h-0.75 bg-[repeating-linear-gradient(90deg,#d97706_0,#d97706_4px,transparent_4px,transparent_7px)]",
                    )}
                  />
                  {span.issue ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-xs"
                      className={cn(
                        "relative z-10 size-5 rounded-full border-[1.5px] border-current bg-card p-0 text-[0.6875rem] font-bold leading-none text-destructive hover:bg-card",
                        (span.severity === "under" ||
                          span.severity === "warn") &&
                          "text-amber-700 dark:text-amber-300",
                        selectedIssueId === span.issue.id &&
                          "ring-2 ring-current/35",
                      )}
                      title={span.issue.summary}
                      aria-label={span.issue.summary}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectIssue(span.issue!.id);
                      }}
                    >
                      {span.severity === "problem" ? "!" : "?"}
                    </Button>
                  ) : (
                    <span className="relative z-10 size-1.5 rounded-full bg-muted-foreground/50" />
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1 pb-0.5 pt-2">
        {TIMELINE_BEAT_KINDS.map((kind) => (
          <span
            key={kind}
            className="inline-flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground"
          >
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: BEAT_KIND_COLORS[kind] }}
            />
            {TIMELINE_BEAT_KIND_LABELS[kind]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground">
          <span className="inline-flex size-4 items-center justify-center rounded-full border border-current bg-card text-[0.625rem] font-bold text-destructive">
            !
          </span>
          Timing problem
        </span>
        <span className="inline-flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground">
          <span className="inline-flex size-4 items-center justify-center rounded-full border border-current bg-card text-[0.625rem] font-bold text-amber-700 dark:text-amber-300">
            ?
          </span>
          Under-described
        </span>
      </div>
    </div>
  );
}

function BeatDetail({
  beat,
  issues,
  chapters,
  onUpdate,
  onRequestRemove,
  onOpenChapter,
  onSelectIssue,
}: {
  beat: BookTimelineBeat;
  issues: TimelineTimingIssue[];
  chapters: Array<{ id: string; order: number; title: string }>;
  onUpdate: (id: string, patch: Partial<BookTimelineBeat>) => void;
  onRequestRemove: (beat: BookTimelineBeat) => void;
  onOpenChapter: (chapterId: string) => void;
  onSelectIssue: (id: string) => void;
}) {
  const linkedIssues = issuesForBeat(issues, beat.id);
  const chapterTitle = (id: string) =>
    chapters.find((c) => c.id === id)?.title ?? id;
  const chapterIds = (
    beat.chapterIds.length ? beat.chapterIds : [beat.chapterId]
  ).filter(Boolean);

  return (
    <div className="flex max-w-184 flex-col gap-3.5 px-4.5 pb-5 pt-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <BeatKindBadge kind={beat.beatKind} />
          <span className="text-xs capitalize text-muted-foreground">
            {beat.narrativePosition}
            {beat.when ? ` · ${beat.when}` : ""}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          type="button"
          onClick={() => onRequestRemove(beat)}
        >
          Remove
        </Button>
      </header>

      <Field label="Summary">
        <Textarea
          aria-label="Beat summary"
          className="bg-card font-medium"
          value={beat.summary}
          rows={3}
          onChange={(e) => onUpdate(beat.id, { summary: e.target.value })}
        />
      </Field>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 min-[900px]:grid-cols-3">
        <Field label="When (as written)">
          <Input
            aria-label="When"
            value={beat.when}
            placeholder="e.g. three days later"
            onChange={(e) => onUpdate(beat.id, { when: e.target.value })}
          />
        </Field>
        <Field label="Sort key">
          <Input
            aria-label="Sort key"
            value={beat.sortKey}
            placeholder="YYYY-MM-DD"
            onChange={(e) => onUpdate(beat.id, { sortKey: e.target.value })}
          />
        </Field>
        <Field label="Kind">
          <Select
            value={beat.beatKind}
            onValueChange={(value) =>
              onUpdate(beat.id, { beatKind: value as TimelineBeatKind })
            }
          >
            <SelectTrigger size="sm" aria-label="Kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMELINE_BEAT_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {TIMELINE_BEAT_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Precision">
          <Select
            value={beat.datePrecision}
            onValueChange={(value) =>
              onUpdate(beat.id, {
                datePrecision: value as TimelineDatePrecision,
              })
            }
          >
            <SelectTrigger size="sm" aria-label="Precision">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMELINE_DATE_PRECISIONS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Narrative position">
          <Select
            value={beat.narrativePosition}
            onValueChange={(value) =>
              onUpdate(beat.id, {
                narrativePosition: value as TimelineNarrativePosition,
              })
            }
          >
            <SelectTrigger size="sm" aria-label="Narrative position">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMELINE_NARRATIVE_POSITIONS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Evidence from manuscript">
        <Textarea
          aria-label="Evidence"
          className="italic text-foreground/90"
          value={beat.evidence ?? ""}
          rows={2}
          placeholder="Short quote or paraphrase…"
          onChange={(e) => onUpdate(beat.id, { evidence: e.target.value })}
        />
      </Field>

      <Field label="Author note">
        <Textarea
          aria-label="Note"
          value={beat.note ?? ""}
          rows={2}
          placeholder="Optional chapter-local detail…"
          onChange={(e) => onUpdate(beat.id, { note: e.target.value })}
        />
      </Field>

      {chapterIds.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4>
            <FieldLabel>Chapters</FieldLabel>
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {chapterIds.map((id) => (
              <Button
                key={id}
                type="button"
                variant="outline"
                size="xs"
                className="rounded-full"
                onClick={() => onOpenChapter(id)}
              >
                {chapterTitle(id)}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {linkedIssues.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4>
            <FieldLabel>Linked timing issues</FieldLabel>
          </h4>
          <ul className="flex list-none flex-col gap-1.5 p-0">
            {linkedIssues.map((issue) => (
              <li key={issue.id}>
                <Button
                  type="button"
                  variant="outline"
                  className="h-auto w-full items-start justify-start gap-2 whitespace-normal px-2.5 py-2 text-left text-xs leading-snug"
                  onClick={() => onSelectIssue(issue.id)}
                >
                  <SeverityBadge severity={issue.severity} />
                  <span>{issue.summary}</span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function IssueDetail({
  issue,
  beats,
  onSelectBeat,
}: {
  issue: TimelineTimingIssue;
  beats: BookTimelineBeat[];
  onSelectBeat: (id: string) => void;
}) {
  return (
    <div className="flex max-w-184 flex-col gap-3.5 px-4.5 pb-5 pt-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SeverityBadge severity={issue.severity} />
          <span className="text-xs capitalize text-muted-foreground">
            {TIMELINE_ISSUE_KIND_LABELS[issue.kind]}
          </span>
        </div>
      </header>
      <h3 className="text-[0.9375rem] font-semibold leading-relaxed">
        {issue.summary}
      </h3>
      {issue.evidence ? (
        <Card size="sm" className="gap-1 bg-muted/20">
          <CardContent className="flex flex-col gap-1">
            <FieldLabel>Evidence</FieldLabel>
            <p className="text-[0.8125rem] leading-relaxed">{issue.evidence}</p>
          </CardContent>
        </Card>
      ) : null}
      {issue.suggestion ? (
        <Card size="sm" className="gap-1 bg-teal-500/5 ring-teal-600/30">
          <CardContent className="flex flex-col gap-1">
            <FieldLabel>Suggestion</FieldLabel>
            <p className="text-[0.8125rem] leading-relaxed">
              {issue.suggestion}
            </p>
          </CardContent>
        </Card>
      ) : null}
      {issue.beatIds.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <h4>
            <FieldLabel>Related beats</FieldLabel>
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {issue.beatIds.map((id) => {
              const beat = beats.find((b) => b.id === id);
              if (!beat) return null;
              return (
                <Button
                  key={id}
                  type="button"
                  variant="outline"
                  size="xs"
                  className="h-auto max-w-full rounded-full whitespace-normal py-1 text-left"
                  onClick={() => onSelectBeat(id)}
                >
                  {truncate(beat.summary, 48)}
                </Button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TimelineGuidePanel({
  roster,
  issues,
  removals,
  chapters,
  selectedId,
  onSelect,
  onUpdate,
  onRequestRemove,
  onAdd,
  holisticBusy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onAnalyzeWholeBook,
  onOpenChapter,
}: {
  roster: BookTimelineBeat[];
  issues: TimelineTimingIssue[];
  removals: TimelineRemovalRecord[];
  chapters: Array<{ id: string; order: number; title: string }>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onUpdate: (id: string, patch: Partial<BookTimelineBeat>) => void;
  onRequestRemove: (beat: BookTimelineBeat) => void;
  onAdd: () => void;
  holisticBusy: boolean;
  extractModel: string;
  onExtractModelChange: (
    model: string,
    provider: "openai" | "anthropic",
  ) => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onAnalyzeWholeBook: () => void;
  onOpenChapter: (chapterId: string) => void;
}) {
  const [orderMode, setOrderMode] = useState<GraphOrderMode>("chrono");
  const [issueFilter, setIssueFilter] = useState<IssueFilter>("all");
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [sideTab, setSideTab] = useState<"beats" | "issues">("beats");

  const chapterOrder = useMemo(() => {
    const map = new Map<string, number>();
    for (const ch of chapters) map.set(ch.id, ch.order);
    return map;
  }, [chapters]);

  const sorted = useMemo(
    () =>
      orderMode === "story"
        ? sortBeatsByStoryOrder(roster, chapterOrder)
        : sortBeatsChronologically(roster, chapterOrder),
    [roster, chapterOrder, orderMode],
  );

  const filteredIssues = useMemo(() => {
    if (issueFilter === "problems") return problematicIssues(issues);
    if (issueFilter === "under_described") return underDescribedIssues(issues);
    return issues;
  }, [issues, issueFilter]);

  const problemCount = problematicIssues(issues).length;
  const underCount = underDescribedIssues(issues).length;

  const selectedBeat =
    sorted.find((b) => b.id === selectedId) ?? sorted[0] ?? null;
  const selectedIssue =
    filteredIssues.find((i) => i.id === selectedIssueId) ??
    filteredIssues[0] ??
    null;

  return (
    <section
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
      aria-label="Timeline guide"
    >
      <Card className="m-1.5 h-full min-h-0 flex-1 gap-0 rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        <PanelHeader
          title="Timeline"
          description="Major pivotal beats — scan chronology, then open timing issues."
          actions={
            <ExtractToolbar
              model={extractModel}
              onModelChange={onExtractModelChange}
              openAiModels={openAiModels}
              anthropicModels={anthropicModels}
              onExtract={onAnalyzeWholeBook}
              extractBusy={holisticBusy}
              extractLabel="Analyze whole book"
              extractBusyLabel="Analyzing…"
              onAdd={onAdd}
              addLabel="Add beat"
            />
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-4 py-2">
          <div
            className="inline-flex rounded-lg border border-border bg-muted p-0.5"
            role="group"
            aria-label="Timeline order"
          >
            <Button
              type="button"
              variant={orderMode === "chrono" ? "outline" : "ghost"}
              size="xs"
              className={cn(
                "border-transparent",
                orderMode === "chrono" &&
                  "border-border bg-card shadow-sm hover:bg-card",
              )}
              onClick={() => setOrderMode("chrono")}
            >
              Chronological
            </Button>
            <Button
              type="button"
              variant={orderMode === "story" ? "outline" : "ghost"}
              size="xs"
              className={cn(
                "border-transparent",
                orderMode === "story" &&
                  "border-border bg-card shadow-sm hover:bg-card",
              )}
              onClick={() => setOrderMode("story")}
            >
              Story order
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>
              {sorted.length} beat{sorted.length === 1 ? "" : "s"}
            </span>
            {issues.length > 0 ? (
              <>
                <span className="opacity-45">·</span>
                <span>
                  {problemCount} problem{problemCount === 1 ? "" : "s"}
                </span>
                <span className="opacity-45">·</span>
                <span>{underCount} under-described</span>
              </>
            ) : (
              <>
                <span className="opacity-45">·</span>
                <span>No timing issues yet</span>
              </>
            )}
          </div>
        </div>

        <BeatChronologyRail
          beats={roster}
          issues={issues}
          chapters={chapters}
          orderMode={orderMode}
          selectedBeatId={selectedBeat?.id ?? null}
          selectedIssueId={selectedIssue?.id ?? null}
          onSelectBeat={(id) => {
            onSelect(id);
            setSideTab("beats");
            setSelectedIssueId(null);
          }}
          onSelectIssue={(id) => {
            setSelectedIssueId(id);
            setSideTab("issues");
          }}
        />

        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(180px,36vh)_1fr] border-t border-border min-[900px]:grid-cols-[minmax(220px,280px)_1fr] min-[900px]:grid-rows-1">
          <Tabs
            value={sideTab}
            onValueChange={(value) => setSideTab(value as "beats" | "issues")}
            className="min-h-0 overflow-auto border-b border-border bg-card min-[900px]:border-b-0 min-[900px]:border-r"
            aria-label="Timeline list"
          >
            <TabsList
              variant="line"
              className="grid shrink-0 grid-cols-2 border-b border-border"
            >
              <TabsTrigger value="beats" variant="line">
                Beats
                <TabsCount>{sorted.length}</TabsCount>
              </TabsTrigger>
              <TabsTrigger value="issues" variant="line">
                Issues
                <TabsCount>{issues.length}</TabsCount>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="beats" className="mt-0">
              {sorted.length === 0 ? (
                <EmptyState
                  compact
                  className="m-3"
                  title="No beats yet"
                  description="Extract from a chapter or analyze the whole book."
                />
              ) : (
                <ul className="m-0 list-none p-0">
                  {sorted.map((beat, index) => (
                    <li key={beat.id}>
                      <Button
                        type="button"
                        variant="ghost"
                        className={cn(
                          "h-auto w-full justify-start gap-2 rounded-none border-b border-border px-3 py-3 text-left whitespace-normal",
                          selectedBeat?.id === beat.id &&
                            "bg-primary/10 hover:bg-primary/15",
                        )}
                        onClick={() => onSelect(beat.id)}
                      >
                        <span
                          className={cn(
                            "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[0.6875rem] font-bold text-muted-foreground",
                            selectedBeat?.id === beat.id &&
                              "bg-primary/20 text-foreground",
                          )}
                          aria-hidden
                        >
                          {index + 1}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <span className="line-clamp-2 text-[0.8125rem] font-semibold leading-snug">
                            {beat.summary}
                          </span>
                          <span className="flex flex-wrap items-center gap-1.5 text-[0.6875rem] text-muted-foreground">
                            <BeatKindBadge kind={beat.beatKind} />
                            <span className="max-w-full truncate">
                              {beat.when || beat.sortKey || "No time cue"}
                            </span>
                          </span>
                        </span>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="issues" className="mt-0">
              <>
                <div className="flex flex-wrap gap-1.5 border-b border-border px-2.5 py-2">
                  {(
                    [
                      ["all", "All"],
                      ["problems", "Problems"],
                      ["under_described", "Vague"],
                    ] as const
                  ).map(([id, label]) => (
                    <Button
                      key={id}
                      type="button"
                      variant={issueFilter === id ? "secondary" : "outline"}
                      size="xs"
                      className="rounded-full"
                      onClick={() => setIssueFilter(id)}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                {filteredIssues.length === 0 ? (
                  <EmptyState
                    compact
                    className="m-3"
                    title="No timing issues yet"
                    description="Run Analyze whole book to flag contradictions and vague stretches."
                  />
                ) : (
                  <ul className="m-0 list-none p-0">
                    {filteredIssues.map((issue) => (
                      <li key={issue.id}>
                        <Button
                          type="button"
                          variant="ghost"
                          className={cn(
                            "h-auto w-full items-start justify-start gap-2 rounded-none border-b border-border px-3 py-3 text-left whitespace-normal",
                            selectedIssue?.id === issue.id &&
                              "bg-primary/10 hover:bg-primary/15",
                          )}
                          onClick={() => setSelectedIssueId(issue.id)}
                        >
                          <SeverityBadge severity={issue.severity} />
                          <span className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="line-clamp-2 text-[0.8125rem] font-semibold leading-snug">
                              {issue.summary}
                            </span>
                            <span className="text-[0.6875rem] text-muted-foreground">
                              {TIMELINE_ISSUE_KIND_LABELS[issue.kind]}
                            </span>
                          </span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            </TabsContent>

            {removals.length > 0 ? (
              <div className="mt-auto border-t border-border px-3 pb-3 pt-2.5">
                <h3>
                  <FieldLabel>Removed</FieldLabel>
                </h3>
                <ul className="mt-1.5 flex list-none flex-col gap-1.5 p-0">
                  {removals.slice(0, 6).map((record) => (
                    <li
                      key={record.id}
                      className="flex flex-col gap-0.5 text-[0.6875rem]"
                    >
                      <strong className="font-semibold">
                        {truncate(record.summary, 40)}
                      </strong>
                      <span className="text-muted-foreground">
                        {record.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Tabs>

          <div className="min-h-0 overflow-auto bg-card">
            {sideTab === "issues" ? (
              selectedIssue ? (
                <IssueDetail
                  issue={selectedIssue}
                  beats={roster}
                  onSelectBeat={(id) => {
                    onSelect(id);
                    setSideTab("beats");
                  }}
                />
              ) : (
                <EmptyState
                  compact
                  className="m-4"
                  title="Select a timing issue"
                />
              )
            ) : selectedBeat ? (
              <BeatDetail
                beat={selectedBeat}
                issues={issues}
                chapters={chapters}
                onUpdate={onUpdate}
                onRequestRemove={onRequestRemove}
                onOpenChapter={onOpenChapter}
                onSelectIssue={(id) => {
                  setSelectedIssueId(id);
                  setSideTab("issues");
                }}
              />
            ) : (
              <EmptyState
                compact
                className="m-4"
                title="Select a beat to view details"
              />
            )}
          </div>
        </div>
      </Card>
    </section>
  );
}
